import React from 'react';
import Image from 'next/legacy/image';

import { getScaledImageHeight } from '../../../utils/getScaledImageHeight';
import { getImageData } from '../../../utils/contentful';

import { GreenlightLogo } from '../../GreenlightLogo';

import glLogoAbbr from '@/images/gl-logo-abbreviated.svg';
import { greenlightBrandingEnum } from '.';
import styles from './AffiliateLogoGrouping.module.css';

interface AffiliateLogoGroupingTypes {
  affiliateLogo: any;
  useGreenlightIcon?: boolean | null;
  greenlightBranding?: greenlightBrandingEnum;
  useAltLayout?: boolean;
  wideSpacing?: boolean;
}

export function AffiliateLogoGrouping({
  affiliateLogo,
  useGreenlightIcon, //TODO: should be deprecated in follow-up ticket,
  greenlightBranding,
  useAltLayout,
  wideSpacing = false
}: AffiliateLogoGroupingTypes) {
  const { src, alt, width, height } = getImageData(affiliateLogo);

  const shouldUseGreenlightBranding = greenlightBranding !== undefined;
  const usingGreenlightIcon =
    useGreenlightIcon ||
    greenlightBranding === greenlightBrandingEnum.GREENLIGHT_ICON;

  const AFFILIATE_LOGO_MIN_HEIGHT_PX = 22;
  const AFFILIATE_LOGO_MAX_HEIGHT_PX = useGreenlightIcon ? 40 : 55;

  const logoWidth = width || 1;
  const logoHeight = height || 1;

  const { imageHeight } = getScaledImageHeight(
    logoWidth,
    logoHeight,
    AFFILIATE_LOGO_MIN_HEIGHT_PX,
    AFFILIATE_LOGO_MAX_HEIGHT_PX
  );

  const affiliateLogoHeight = `${parseInt(imageHeight) * 2}rem`;
  const leftBoxBase = usingGreenlightIcon
    ? 'flex justify-center desktop:justify-start'
    : 'block';

  return (
    <div className="mx-auto flex min-h-[88px] w-[90%] flex-row items-center justify-center tablet:m-0 tablet:justify-start desktop:min-h-0">
      <div
        className={`${leftBoxBase} ${
          useAltLayout ? '' : 'flex-[0.1_1_auto]'
        } flex h-full items-center justify-center desktop:flex-none`}
      >
        {shouldUseGreenlightBranding ? (
          greenlightBranding === greenlightBrandingEnum.GREENLIGHT_ICON ? (
            <Image
              className={`mx-auto max-w-full desktop:mx-0 ${
                useAltLayout ? 'max-h-[40px]' : ''
              }`}
              src={glLogoAbbr}
              alt="Greenlight abbreviated logo"
            />
          ) : greenlightBranding === greenlightBrandingEnum.GREENLIGHT_LOGO ? (
            <GreenlightLogo
              isMenuOpen={false}
              className={`mx-auto max-w-full desktop:mx-0 ${
                useAltLayout ? 'max-h-[40px]' : ''
              }`}
            />
          ) : null
        ) : useGreenlightIcon ? (
          <Image
            className={`mx-auto max-w-full desktop:mx-0 ${
              useAltLayout ? 'max-h-[40px]' : ''
            }`}
            src={glLogoAbbr}
            alt="Greenlight abbreviated logo"
          />
        ) : (
          <GreenlightLogo
            isMenuOpen={false}
            className={`mx-auto max-w-full desktop:mx-0 ${
              useAltLayout ? 'max-h-[40px]' : ''
            }`}
          />
        )}
      </div>
      {greenlightBranding !== greenlightBrandingEnum.NONE && (
        <div
          className={`${
            wideSpacing
              ? styles['affiliate-logo-divider-container']
              : 'mx-2 desktop:mx-3'
          } my-auto max-h-[40px] overflow-hidden`}
        >
          <div
            className={`h-10 w-px ${
              useAltLayout
                ? 'bg-shadow'
                : 'outline outline-1 outline-offset-[-0.50px] outline-shadow'
            }`}
          />
        </div>
      )}
      <div
        className={`${leftBoxBase} ${
          useAltLayout ? '' : 'flex-[0.1_1_auto]'
        } flex h-full items-center justify-center desktop:flex-none`}
      >
        <img
          src={src}
          style={
            useAltLayout
              ? undefined
              : {
                  maxHeight:
                    usingGreenlightIcon ||
                    greenlightBranding === greenlightBrandingEnum.NONE
                      ? imageHeight
                      : affiliateLogoHeight
                }
          }
          className={`max-w-full ${useAltLayout ? 'max-h-[40px]' : ''}`}
          alt={(alt ?? '') || 'Affiliate of Greenlight logo'}
        />
      </div>
    </div>
  );
}
